import i18n from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { languageCodes, countryCodes, phoneCountryCodeToFlag } from '@/shared/languageCodes';

export const formatPhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) {
    return '';
  }
  return parsePhoneNumberFromString(phoneNumber)?.formatInternational() ?? phoneNumber;
};

/**
 * Extracts the country code from a phone number and returns the corresponding flag emoji.
 *
 * @param {string} phoneNumber - The phone number in international format (e.g., +1234567890).
 * @returns {string | null} - The flag emoji if found, else null.
 */
export const getFlagByPhoneNumber = (phoneNumber?: string): string | null => {
  if (!phoneNumber) {
    return null;
  }

  // Remove the + sign and any non-digit characters
  const cleaned = phoneNumber.replace(/^\+/, '').replace(/\D/g, '');

  // Try to match country codes from longest to shortest (1-3 digits)
  for (let i = 3; i >= 1; i--) {
    const code = cleaned.substring(0, i);
    if (phoneCountryCodeToFlag[code]) {
      return phoneCountryCodeToFlag[code];
    }
  }

  return null;
};

/**
 * Retrieves the language name for a given locale.
 *
 * @param {string} [locale] - The locale string in the format language-COUNTRY.
 * @returns {string | null} - The language name if found, else null.
 */
export const getLanguageByLocale = (locale?: string): string | null => {
  if (!locale) {
    return null;
  }
  const code = locale.split('-')[0].toLowerCase();
  return languageCodes[code] || null;
};

/**
 * Retrieves the country name by its code.
 *
 * @param {string} [code] - The country code.
 * @returns {string | null} - The country name if found, else null.
 */
export const getCountryByCode = (code?: string): string | null => {
  if (!code) {
    return null;
  }
  return countryCodes[code.toLowerCase()]?.name || null;
};

/**
 * Gets the timezone offset in the format "+HH:MM" or "-HH:MM"
 */
const getTimezoneOffset = (timezone: string): string => {
  const now = new Date();

  // Helper to get time parts in a given timezone
  const getTimeParts = (date: Date, timeZone: string) => {
    const dtf = new Intl.DateTimeFormat('en-US', {
      hour12: false,
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = dtf.formatToParts(date);
    const get = (type: string) => {
      const part = parts.find((p) => p.type === type);
      return part ? parseInt(part.value, 10) : 0;
    };
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
      second: get('second'),
    };
  };

  const utcParts = getTimeParts(now, 'UTC');
  const tzParts = getTimeParts(now, timezone);

  // Create Date objects from the extracted parts (in local time)
  const utcDate = new Date(
    utcParts.year,
    utcParts.month - 1,
    utcParts.day,
    utcParts.hour,
    utcParts.minute,
    utcParts.second
  );
  const tzDate = new Date(
    tzParts.year,
    tzParts.month - 1,
    tzParts.day,
    tzParts.hour,
    tzParts.minute,
    tzParts.second
  );

  // Offset in minutes
  const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60)
    .toString()
    .padStart(2, '0');
  const minutes = Math.round(absMinutes % 60)
    .toString()
    .padStart(2, '0');

  return `${sign}${hours}:${minutes}`;
};

/**
 * Converts timezones to a desired format for Business Hours and Rules.
 *
 * @param {string[]} timezones - Array of timezones e.g. ["Europe/Budapest"].
 * @returns {Array<Object>} - An array of objects with formatted timezone labels and values.
 */
export const timezoneMaker = (timezones: string[]) => {
  if (timezones.length === 0) {
    return [];
  }

  return timezones.map((timezone) => {
    const split = timezone.split('/');
    const lastItem = split[split.length - 1];
    const final = lastItem.split(/[-_ ]/).join('_').toLowerCase();
    return {
      label: `(GMT${getTimezoneOffset(timezone)}) ${i18n.t(final, { ns: 'timezones' })}`,
      value: timezone,
    };
  });
};
